import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { PenLine, Users, ShieldCheck, AlertTriangle, Scale, MapPin, FileCheck2, Share2, ExternalLink, Printer } from "lucide-react";
import api from "@/lib/api";
import { Loading, Crumbs, ShareMenu } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

const STATUS_LABELS = {
  publicada: "Recolectando firmas", meta_alcanzada: "Meta alcanzada", entregada: "Entregada",
  recibida: "Recibida oficialmente", esperando_respuesta: "Esperando respuesta", contestada: "Contestada",
  accion_tomada: "Acción tomada", cerrada: "Cerrada",
};

const MODES = [
  { k: "public_name", label: "Mostrar mi nombre públicamente" },
  { k: "initials", label: "Mostrar solo mis iniciales" },
  { k: "verified_citizen", label: "Mostrar “Ciudadano verificado”" },
  { k: "private", label: "Firma privada (contabilizada, no visible)" },
];

export default function PetitionDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [showSign, setShowSign] = useState(false);
  const [mode, setMode] = useState("verified_citizen");
  const [territory, setTerritory] = useState("");
  const [busy, setBusy] = useState(false);
  const { user, login } = useAuth();

  const load = useCallback(() => api.get(`/petitions/${id}`).then((r) => setP(r.data)).catch(() => setP(false)), [id]);
  useEffect(() => { load(); }, [load]);

  if (p === null) return <Loading />;
  if (p === false) return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-slate-400">Petición no encontrada.</div>;

  const s = p.signatures;
  const pct = Math.min(100, Math.round((s.verified / p.goal) * 100));
  const alreadySigned = p.user_signed?.signed;

  const sign = async () => {
    if (!user) { toast.info("Crea tu cuenta verificada para firmar."); login(); return; }
    setBusy(true);
    try {
      const r = await api.post(`/petitions/${id}/sign`, { display_mode: mode, territory: territory || undefined });
      toast.success("Tu firma fue registrada.");
      setShowSign(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo firmar.");
    } finally { setBusy(false); }
  };

  const openDossier = async () => {
    try {
      const r = await api.get(`/petitions/${id}/dossier`);
      const d = r.data;
      const w = window.open("", "_blank");
      if (!w) { toast.error("Permite las ventanas emergentes para ver el expediente."); return; }
      const doc = w.document;
      doc.title = `Expediente — ${d.title}`;
      const style = doc.createElement("style");
      style.textContent = "body{font-family:Inter,system-ui,sans-serif;max-width:720px;margin:40px auto;padding:0 20px;color:#0f172a;line-height:1.6}h1{font-size:22px}small{color:#64748b}.box{border:1px solid #e2e8f0;border-radius:10px;padding:16px;margin:16px 0}.code{font-family:monospace;background:#f1f5f9;padding:4px 8px;border-radius:6px}pre{white-space:pre-wrap;font-family:inherit;margin:0}";
      doc.head.appendChild(style);
      // Build DOM with textContent only (no HTML injection) to avoid XSS from petition content.
      const el = (tag, text, cls) => { const e = doc.createElement(tag); if (text != null) e.textContent = text; if (cls) e.className = cls; return e; };
      const line = (label, value) => { const p = el("div"); p.appendChild(el("strong", `${label}: `)); p.appendChild(doc.createTextNode(String(value))); return p; };
      const b = doc.body;
      b.appendChild(el("small", "2Voto — Expediente de petición"));
      b.appendChild(el("h1", d.title));
      const box1 = el("div", null, "box");
      box1.appendChild(line("Dirigida a", d.addressed_to));
      box1.appendChild(line("Acción solicitada", d.action_requested));
      b.appendChild(box1);
      b.appendChild(el("pre", d.text));
      const box2 = el("div", null, "box");
      box2.appendChild(line("Firmas verificadas", d.verified_signatures.toLocaleString("es-PR")));
      box2.appendChild(line("Firmas totales", d.total_signatures.toLocaleString("es-PR")));
      box2.appendChild(line("Excluidas por irregularidad/duplicidad", d.rejected));
      box2.appendChild(line("Territorios representados", d.territories.join(", ")));
      box2.appendChild(line("Generado", new Date(d.generated_at).toLocaleString("es-PR")));
      const codeLine = el("div");
      codeLine.appendChild(el("strong", "Código de autenticidad: "));
      codeLine.appendChild(el("span", d.authenticity_code, "code"));
      box2.appendChild(codeLine);
      b.appendChild(box2);
      const note = el("p"); note.appendChild(el("small", d.methodology)); b.appendChild(note);
      const btn = el("button", "Imprimir / Guardar PDF");
      btn.onclick = () => w.print();
      b.appendChild(btn);
    } catch (e) { toast.error("No se pudo generar el expediente."); }
  };

  const share = () => { navigator.clipboard.writeText(window.location.href); toast.success("Enlace copiado."); };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="petition-detail">
      <Crumbs items={[{ label: "2Voto Peticiones", to: "/peticiones" }, { label: p.title }]} />
      <div className="flex justify-end -mt-2 mb-2"><ShareMenu title={p.title} /></div>

      <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-cyan-100 text-cyan-800 mb-3" data-testid="petition-status">{STATUS_LABELS[p.status] || p.status}</span>
      <h1 className="font-heading font-black text-3xl md:text-4xl text-slate-900 tracking-tight mb-5">{p.title}</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        <div className="bg-white rounded-lg border border-slate-200 p-4"><p className="text-xs uppercase tracking-wide text-slate-400">Dirigida a</p><p className="font-semibold text-slate-900">{p.addressed_to}</p></div>
        <div className="bg-white rounded-lg border border-slate-200 p-4"><p className="text-xs uppercase tracking-wide text-slate-400">Acción solicitada</p><p className="font-semibold text-slate-900">{p.action_requested}</p></div>
      </div>

      {p.presumption_notice && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3 mb-6" data-testid="presumption-notice">
          <Scale className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">Firmar esta petición no constituye una declaración de culpabilidad. Rige la presunción de inocencia: una investigación no equivale a una determinación de culpabilidad. Representa una solicitud de prudencia institucional mientras continúa el proceso.</p>
        </div>
      )}

      {/* Progress + sign */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
        <div className="flex items-end justify-between mb-2">
          <div>
            <p className="font-heading font-black text-3xl text-slate-900">{s.verified.toLocaleString("es-PR")}</p>
            <p className="text-sm text-slate-500">firmas verificadas · meta {p.goal.toLocaleString("es-PR")}</p>
          </div>
          {p.deadline && <p className="text-sm text-slate-400">Cierra: {new Date(p.deadline).toLocaleDateString("es-PR")}</p>}
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden mb-4">
          <div className="h-full bg-cyan-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
        {alreadySigned ? (
          <div className="flex items-center gap-2 text-cyan-700 font-semibold" data-testid="already-signed"><ShieldCheck className="w-5 h-5" /> Ya firmaste esta petición ({MODES.find(m => m.k === p.user_signed.display_mode)?.label.toLowerCase()}).</div>
        ) : showSign ? (
          <div className="space-y-3" data-testid="sign-panel">
            <p className="text-sm font-semibold text-slate-700">¿Cómo quieres que aparezca tu firma?</p>
            {MODES.map((m) => (
              <label key={m.k} className="flex items-center gap-3 cursor-pointer">
                <input type="radio" name="signmode" checked={mode === m.k} onChange={() => setMode(m.k)} data-testid={`sign-mode-${m.k}`} className="accent-cyan-500 w-4 h-4" />
                <span className="text-sm text-slate-700">{m.label}</span>
              </label>
            ))}
            <input value={territory} onChange={(e) => setTerritory(e.target.value)} data-testid="sign-territory"
              placeholder="Municipio o región (opcional)" className="w-full border border-slate-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-cyan-500" />
            <div className="flex gap-3">
              <button onClick={sign} disabled={busy} data-testid="confirm-sign"
                className="bg-rose-500 text-white font-semibold px-6 py-2.5 rounded-full hover:bg-rose-600 transition-colors disabled:opacity-60">
                {busy ? "Firmando…" : "Confirmar firma"}
              </button>
              <button onClick={() => setShowSign(false)} className="text-slate-500 font-medium px-4">Cancelar</button>
            </div>
            <p className="text-xs text-slate-400">2Voto verifica internamente tu autenticidad sin publicar tu email, teléfono, afiliación política ni empleo.</p>
          </div>
        ) : (
          <button onClick={() => (user ? setShowSign(true) : login())} data-testid="sign-button"
            className="inline-flex items-center gap-2 bg-rose-500 text-white font-semibold px-6 py-3 rounded-full hover:bg-rose-600 transition-colors">
            <PenLine className="w-4 h-4" /> Firmar petición
          </button>
        )}
      </div>

      {/* Petition text */}
      <div className="prose prose-slate max-w-none mb-8">
        <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">Texto de la petición</h3>
        <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{p.justification}</p>
      </div>

      {/* Transparency metrics */}
      <div className="bg-slate-100 rounded-xl p-6 mb-8" data-testid="petition-metrics">
        <h3 className="flex items-center gap-2 font-heading font-bold text-lg text-slate-900 mb-4"><Users className="w-5 h-5" /> Transparencia de firmas</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[
            { n: s.total, l: "Firmas totales" },
            { n: s.verified, l: "Verificadas" },
            { n: s.public, l: "Públicas" },
            { n: s.private, l: "Privadas" },
            { n: s.rejected, l: "Excluidas (duplicidad/irregular)" },
            { n: s.territories.length, l: "Territorios representados" },
          ].map((m) => (
            <div key={m.l} className="bg-white rounded-lg p-3">
              <p className="font-heading font-black text-xl text-slate-900">{m.n.toLocaleString("es-PR")}</p>
              <p className="text-[11px] text-slate-500">{m.l}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Official response + timeline */}
      {(p.official_response || (p.timeline && p.timeline.length > 0)) && (
        <div className="mb-8">
          <h3 className="font-heading font-bold text-lg text-slate-900 mb-3">Seguimiento</h3>
          {p.official_response && (
            <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
              <p className="text-xs uppercase tracking-wide text-cyan-600 font-semibold mb-1">Respuesta oficial</p>
              <p className="text-slate-700 text-sm whitespace-pre-wrap">{p.official_response}</p>
            </div>
          )}
          {p.timeline?.length > 0 && (
            <div className="border-l-2 border-cyan-200 pl-5 space-y-4">
              {p.timeline.map((t) => (
                <div key={`${t.date}-${t.event}`} className="relative">
                  <span className="absolute -left-[27px] top-1 w-3 h-3 rounded-full bg-cyan-500 border-2 border-white" />
                  <p className="text-sm font-semibold text-slate-900">{t.date}</p>
                  <p className="text-sm text-slate-600">{t.event}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recent signers */}
      {p.recent_signers?.length > 0 && (
        <div className="mb-8">
          <h3 className="font-heading font-bold text-lg text-slate-900 mb-3">Firmantes recientes</h3>
          <div className="flex flex-wrap gap-2">
            {p.recent_signers.slice(0, 30).map((r, i) => (
              <span key={`${r.label}-${r.territory || ""}-${i}`} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-600 text-sm px-3 py-1.5 rounded-full">
                <ShieldCheck className="w-3.5 h-3.5 text-cyan-500" /> {r.label}{r.territory ? ` · ${r.territory}` : ""}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Sources + actions */}
      {p.sources?.length > 0 && (
        <div className="mb-6">
          <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">Fuentes</h3>
          {p.sources.map((src) => (
            <a key={src} href={src} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-cyan-600 hover:underline break-all mb-1">
              <ExternalLink className="w-3.5 h-3.5 shrink-0" /> {src}
            </a>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <button onClick={openDossier} data-testid="dossier-button" className="inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-slate-800 transition-colors">
          <FileCheck2 className="w-4 h-4" /> Ver expediente
        </button>
        <button onClick={share} className="inline-flex items-center gap-2 border border-slate-200 text-slate-700 text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-slate-100 transition-colors">
          <Share2 className="w-4 h-4" /> Compartir
        </button>
      </div>
    </div>
  );
}
